// -----------------------------------------------------------
// FloraIQ - ValidationStagePanel (Aba Etapa 6 - Validação)
// -----------------------------------------------------------

import { useState } from "react";
import { validationItems, validationAlertCards } from "@/mocks/operational";

const severityColors: Record<string, string> = {
  "Crítica": "bg-red-100 text-red-700",
  "Alta": "bg-orange-100 text-orange-700",
  "Média": "bg-yellow-100 text-yellow-700",
  "Baixa": "bg-blue-100 text-blue-700",
  "Informativa": "bg-background-100 text-foreground-600",
};

const statusVColors: Record<string, string> = {
  "Conforme": "bg-green-100 text-green-700",
  "Em validação": "bg-accent-100 text-accent-700",
  "Pendente": "bg-orange-100 text-orange-700",
};

export default function ValidationStagePanel() {
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(false);

  const handleValidate = () => {
    setValidating(true);
    setValidated(false);
    setTimeout(() => {
      setValidating(false);
      setValidated(true);
      setTimeout(() => setValidated(false), 3000);
    }, 1500);
  };

  return (
    <div className="space-y-5">
      {/* Alert cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {validationAlertCards.map((card) => (
          <div key={card.titulo} className={`rounded-card border border-background-200/70 p-3 ${
            card.variant === "danger" ? "bg-red-50/50" : card.variant === "warning" ? "bg-orange-50/50" : "bg-green-50/50"
          }`}>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                card.variant === "danger" ? "bg-red-100 text-red-600" : card.variant === "warning" ? "bg-orange-100 text-orange-600" : "bg-green-100 text-green-600"
              }`}>
                <i className={`${card.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-[10px] text-foreground-500">{card.titulo}</p>
                <p className="text-sm font-bold text-foreground-900">{card.valor}</p>
              </div>
            </div>
            <p className="text-[10px] text-foreground-400">{card.descricao}</p>
          </div>
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={handleValidate} disabled={validating} className={`text-xs px-3 py-2 rounded-lg font-medium cursor-pointer whitespace-nowrap transition-all ${
          validating ? "bg-background-200 text-foreground-400 cursor-wait" : "bg-primary-500 text-white hover:bg-primary-600"
        }`}>
          {validating ? (
            <><i className="ri-loader-4-line animate-spin mr-1"></i>Validando...</>
          ) : (
            <><i className="ri-check-double-line mr-1"></i>Executar Validação</>
          )}
        </button>
        <button className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
          <i className="ri-error-warning-line mr-1"></i>Abrir Inconsistências
        </button>
        <button className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
          <i className="ri-tools-line mr-1"></i>Corrigir em Lote
        </button>
      </div>

      {validated && (
        <div className="slide-up p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 flex items-center gap-2">
          <i className="ri-check-double-line text-green-600"></i>
          Validação concluída! 10 regras verificadas sobre 12.486 registros. 86% de conformidade.
        </div>
      )}

      {/* Validation table */}
      <div className="rounded-card border border-background-200/70 p-4">
        <h4 className="text-sm font-bold text-foreground-900 mb-3">Resultados da Validação</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/50">
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Regra</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Avaliado</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Conformes</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Inconformes</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Severidade</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Status</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Ação</th>
              </tr>
            </thead>
            <tbody>
              {validationItems.map((v) => (
                <tr key={v.id} className="border-b border-background-100/70 hover:bg-background-50/50">
                  <td className="px-2 py-2 text-xs text-foreground-700">{v.regra}</td>
                  <td className="px-2 py-2 text-xs text-foreground-600">{v.totalAvaliado.toLocaleString("pt-BR")}</td>
                  <td className="px-2 py-2 text-xs font-medium text-green-700">{v.conformes.toLocaleString("pt-BR")}</td>
                  <td className="px-2 py-2">
                    <span className={`text-xs font-medium ${v.inconformes > 100 ? "text-red-600" : v.inconformes > 10 ? "text-orange-600" : "text-foreground-600"}`}>
                      {v.inconformes.toLocaleString("pt-BR")}
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${severityColors[v.severidade] || ""}`}>{v.severidade}</span>
                  </td>
                  <td className="px-2 py-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${statusVColors[v.status] || ""}`}>{v.status}</span>
                  </td>
                  <td className="px-2 py-2 text-[10px] text-foreground-500 max-w-[140px] truncate">{v.acaoRecomendada}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}